import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../ui/dialog";
import { Button } from "../../../../ui/button";
import { Input } from "../../../../ui/input";
import { Label } from "../../../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { RadioGroup, RadioGroupItem } from "../../../../ui/radio-group";
import { toast } from "../../../../../hooks/use-toast";
import { supabase } from "../../../../../integrations/supabase/client";
import { Loader2, Copy } from "lucide-react";
import { Badge } from "../../../../ui/badge";
import { apiUrl, clientheaderconfig } from "../../../../Helpers/Config";

interface DataForSEOOutletModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  outlet?: any; // For edit mode
  outletList:string[];
  onRecallOulet: (recall: boolean) => void;

}

export const DataForSEOOutletModal = ({ open, onOpenChange, onSuccess, outlet, outletList, onRecallOulet }: DataForSEOOutletModalProps) => {
 
  const isEditMode = !!outlet;
  const [outletName, setOutletName] = useState("");
  const [branch, setBranch] = useState("");
  const [locationCode, setLocationCode] = useState(2702); // Singapore default
  const [languageName, setLanguageName] = useState("English");
  const [fetchMode, setFetchMode] = useState<"keyword" | "place_id">("place_id");
  const [input, setInput] = useState("");
  const [reviewLimit, setReviewLimit] = useState(10);
  const [verifying, setVerifying] = useState(false);
  const [tripAdvisorUrl, setTripAdvisorUrl] = useState("");
  const [tripAdvisorDepth, setTripAdvisorDepth] = useState(30);
  const [tripAdvisorScraping, setTripAdvisorScraping] = useState(false);
  const [facebookUrl, setFacebookUrl] = useState("");
  const [facebookScraping, setFacebookScraping] = useState(false);
  const [epicOutletId, setEpicOutletId] = useState("");
  const [epicScraping, setEpicScraping] = useState(false);
  const [webhookSecret, setWebhookSecret] = useState("");

  // Populate form when editing
  useEffect(() => {
    if (outlet && open) {
      const foundItem = (outletList as any[]).find((item) => item.supabaseOutletID === outlet.id);
      const outletid = foundItem ? foundItem._id : '';
      const outletAddress = foundItem ? foundItem.outletAddress : '';
      setOutletName(outletid || "");
      setBranch(outletAddress || "");
      setLocationCode(outlet.location_code || 2702);
      setFetchMode(outlet.data_fetch_mode || "place_id");
      setInput(outlet.place_id || "");
      setReviewLimit(outlet.fetch_limit || 10);
      setTripAdvisorUrl(outlet.tripadvisor_url || "");
      setTripAdvisorDepth(30);
      setFacebookUrl(outlet.facebook_url || "");
      setEpicOutletId(outlet.epic_outlet_id || "");
      setWebhookSecret(outlet.webhook_secret || "");
    }
    if(!open) {
      console.log('reset');
      resetForm();
    }
  }, [outlet, open]);
  useEffect(() => {
    const foundItem = (outletList as any[]).find((item) => item._id === outletName);
    const outletAddress = foundItem ? foundItem.outletAddress : '';
    setBranch(outletAddress || "");
    const outletPlaceID = foundItem ? foundItem.outletPlaceID : '';
    setInput(outletPlaceID || "");
    const outletID__ = foundItem ? foundItem._id : '';
    setEpicOutletId(outletID__ || '');
  }, [outletName]);

  const locationOptions = [
    { code: 2702, name: "Singapore", countryCode: "SG" },
    { code: 2356, name: "India", countryCode: "IN" },
    { code: 2840, name: "United States", countryCode: "US" },
    { code: 2826, name: "United Kingdom", countryCode: "GB" },
  ];

  const extractPlaceIdFromUrl = (url: string): string | null => {
    const match = url.match(/ChI[A-Za-z0-9_-]+/);
    return match ? match[0] : null;
  };

  const extractTripAdvisorUrlPath = (url: string): string | null => {
    // Extract the Restaurant_Review... part from TripAdvisor URL
    const match = url.match(/Restaurant_Review[^/]+/);
    return match ? match[0] : null;
  };

  const startTripAdvisorScraping = async (outletId: string) => {
    if (!tripAdvisorUrl) {
      toast({
        title: "Error",
        description: "Please enter a TripAdvisor URL",
        variant: "destructive",
      });
      return;
    }

    const urlPath = extractTripAdvisorUrlPath(tripAdvisorUrl);
    if (!urlPath) {
      toast({
        title: "Invalid TripAdvisor URL",
        description: "Please enter a valid TripAdvisor restaurant URL",
        variant: "destructive",
      });
      return;
    }

    setTripAdvisorScraping(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Update outlet with TripAdvisor URL
      const { error: updateError } = await supabase
        .from('outlets')
        .update({ tripadvisor_url: tripAdvisorUrl })
        .eq('id', outletId);

      if (updateError) throw updateError;

      // Create a scraping job
      const { data: jobData, error: jobError } = await supabase
        .from('scraping_jobs')
        .insert({
          user_id: user.id,
          outlet_id: outletId,
          platform: 'tripadvisor',
          source: 'DATAFORSEO',
          status: 'queued',
          message: 'TripAdvisor scraping started',
          triggered_by: 'manual'
        })
        .select()
        .single();

      if (jobError) throw jobError;

      // Call the edge function asynchronously
      supabase.functions.invoke('tripadvisor-fetch-reviews', {
        body: {
          url_path: urlPath,
          language_name: 'English',
          depth: tripAdvisorDepth,
          jobId: jobData.id,
          outlet_id: outletId,
        },
      }).then(response => {
        console.log("TripAdvisor scraping response:", response);
        if (response.error) {
          console.error("TripAdvisor scraping error:", response.error);
        }
      }).catch(error => {
        console.error("TripAdvisor scraping error:", error);
      });

      toast({
        title: "✅ TripAdvisor Scraping Started",
        description: `Fetching up to ${tripAdvisorDepth} reviews. Check Sync Center for progress.`,
      });
    } catch (error: any) {
      console.error("TripAdvisor scraping error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to start TripAdvisor scraping",
        variant: "destructive",
      });
    } finally {
      setTripAdvisorScraping(false);
    }
  };

  const startFacebookScraping = async (outletId: string) => {
    if (!facebookUrl) {
      toast({
        title: "Error",
        description: "Please enter a Facebook Page URL",
        variant: "destructive",
      });
      return;
    }

    setFacebookScraping(true);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Update outlet with Facebook URL
      const { error: updateError } = await supabase
        .from('outlets')
        .update({ facebook_url: facebookUrl })
        .eq('id', outletId);

      if (updateError) throw updateError;

      // Create a scraping job
      const { data: jobData, error: jobError } = await supabase
        .from('scraping_jobs')
        .insert({
          user_id: user.id,
          outlet_id: outletId,
          platform: 'facebook',
          source: 'APIFY',
          status: 'queued',
          message: 'Facebook scraping started',
          triggered_by: 'manual'
        })
        .select()
        .single();

      if (jobError) throw jobError;

      // Call the edge function asynchronously
      supabase.functions.invoke('scrape-facebook', {
        body: {
          outletId,
          facebookUrl,
          jobId: jobData.id,
        },
      }).then(response => {
        console.log("Facebook scraping response:", response);
        if (response.error) {
          console.error("Facebook scraping error:", response.error);
        }
      }).catch(error => {
        console.error("Facebook scraping error:", error);
      });

      toast({
        title: "✅ Facebook Scraping Started",
        description: "Fetching reviews. Check Sync Center for progress.",
      });
    } catch (error: any) {
      console.error("Facebook scraping error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to start Facebook scraping",
        variant: "destructive",
      });
    } finally {
      setFacebookScraping(false);
    }
  };

  const startEpicReviewsScraping = async (outletId: string) => {
    if (!epicOutletId) {
      toast({
        title: "Error",
        description: "Please enter an EpicPay Outlet ID",
        variant: "destructive",
      });
      return;
    }

    const bearerToken = localStorage.getItem('epic_bearer_token');
    if (!bearerToken) {
      toast({
        title: "Error",
        description: "Please set Bearer Token in Settings → EpicReviews API first",
        variant: "destructive",
      });
      return;
    }

    setEpicScraping(true);

    try {
      const { data, error } = await supabase.functions.invoke('epicreviews-fetch-reviews', {
        body: {
          outletId,
          bearerToken,
        },
      });

      if (error) throw error;

      if (data.status === 'ok') {
        toast({
          title: "✅ EpicReviews Fetch Started",
          description: `Fetching ${data.resultCount || 0} low-star reviews. Check Sync Center.`,
        });
      } else {
        throw new Error(data.error || 'Unknown error');
      }
    } catch (error: any) {
      console.error("EpicReviews error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to fetch EpicReviews",
        variant: "destructive",
      });
    } finally {
      setEpicScraping(false);
    }
  };

  const handleVerify = async () => {

 
    const foundItem = (outletList as any[]).find((item) => item._id === outletName);
    const outeltName_ = foundItem ? foundItem.label : null;
  

    if (!outletName) {
      toast({
        title: "Validation Error",
        description: "Outlet name is required",
        variant: "destructive",
      });
      return;
    }

    if (!input) {
      toast({
        title: "Validation Error",
        description: fetchMode === "place_id" ? "Place ID is required" : "Keyword is required",
        variant: "destructive",
      });
      return;
    }

    setVerifying(true);

    try {
      let placeId = null;
      /* let keyword = null; */

      if (fetchMode === "place_id") {
        // Try to extract place_id from URL if provided
        placeId = input.includes("google.com/maps") ? extractPlaceIdFromUrl(input) : input;
        
        if (!placeId) {
          throw new Error("Invalid Place ID format. Please provide a valid Place ID or Google Maps URL.");
        }
      } /* else {
        keyword = input;
      } */

      // First save the outlet
      const userId = (await supabase.auth.getUser()).data.user?.id;
      console.log(await supabase.auth.getUser(), 'userIduserIduserIduserId')
      
      const outletData = {
        outlet_name: outeltName_,
        branch: branch || null,
        business_name: outeltName_, // Will be updated after verification
        data_source: "dataforseo",
        data_fetch_mode: fetchMode,
        place_id: placeId,
        country_code: locationOptions.find(l => l.code === locationCode)?.countryCode || "SG",
        location: locationOptions.find(l => l.code === locationCode)?.name || "Singapore",
        location_code: locationCode,
        fetch_limit: reviewLimit,
        verified_status: false,
        user_id: userId,
      };

      let outletId: string;

      if (isEditMode && outlet?.id) {
        // Update existing outlet
        const { error: outletError } = await supabase
          .from("outlets")
          .update(outletData)
          .eq("id", outlet.id);

        if (outletError) throw outletError;
        outletId = outlet.id;
        updatesupabase(outlet.id)
        
      } else {
        // Create new outlet
        const { data: outletInsertData, error: outletError } = await supabase
          .from("outlets")
          .insert(outletData)
          .select()
          .single();

        if (outletError) throw outletError;
        outletId = outletInsertData.id;
        updatesupabase(outletId)
      }

      // Create a background job in scraping_jobs
      const { data: jobData, error: jobError } = await supabase
        .from("scraping_jobs")
        .insert({
          user_id: userId,
          outlet_id: outletId,
          platform: "google_maps",
          status: "queued",
          message: "Verification in progress...",
          source: "dataforseo",
          triggered_by: "manual",
        })
        .select()
        .single();

      if (jobError) throw jobError;

      // Update outlet with TripAdvisor URL if provided
      if (tripAdvisorUrl) {
        await supabase
          .from('outlets')
          .update({ tripadvisor_url: tripAdvisorUrl })
          .eq('id', outletId);
      }

      // Update outlet with Facebook URL if provided
      if (facebookUrl) {
        await supabase
          .from('outlets')
          .update({ facebook_url: facebookUrl })
          .eq('id', outletId);
      }

      // Update outlet with EpicPay Outlet ID if provided
      if (epicOutletId) {
        await supabase
          .from('outlets')
          .update({ epic_outlet_id: epicOutletId })
          .eq('id', outletId);
      }

      // Call scrape-outlet asynchronously (no await)
      supabase.functions.invoke("scrape-outlet", {
        body: {
          outletId,
          jobId: jobData.id,
          background: true,
        },
      }).catch(error => {
        console.error("Background job error:", error);
      });

      // Start TripAdvisor scraping if URL is provided
      if (tripAdvisorUrl) {
        startTripAdvisorScraping(outletId);
      }

      // Start Facebook scraping if URL is provided
      if (facebookUrl) {
        startFacebookScraping(outletId);
      }

      // Start EpicReviews scraping if ID is provided
      if (epicOutletId) {
        startEpicReviewsScraping(outletId);
      }

      toast({
        title: "✅ Verification Started",
        description: "View progress in Sync Center",
      });

      // Close modal and trigger success callback
      onSuccess?.();
      onOpenChange(false);
      resetForm();
    } catch (error: any) {
      console.error("❌ Verification Error:", error);
      
      // Check for duplicate constraint violation
      if (error.message?.includes('outlets_user_place_unique') || error.code === '23505') {
        toast({
          title: "Duplicate Outlet",
          description: "This outlet already exists for your account.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Verification Failed",
          description: error.message,
          variant: "destructive",
        });
      }
    } finally {
      setVerifying(false);
    }
  };

  const updatesupabase = (supabaseID:any) => {
      const postObject = {
        outletID: outletName,
        supabaseID
      };
      axios
        .post(apiUrl + "outlet/updatesupabase", postObject, clientheaderconfig)
        .then((res) => {
          console.log(res.data, "res.datares.datares.data");
          onRecallOulet(true)
        })
        .catch((e) => {
          console.log(e);
        });
  }

  const updateWebhooksupabase = (secret:any) => {
      const postObject = {
        outletID: outletName,
        supabaseIDType:'reviewebhook',
        reviewSecretKey:secret,
        reviewWebhook:`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/epic-review-webhook`,
      };
      axios
        .post(apiUrl + "outlet/updatesupabase", postObject, clientheaderconfig)
        .then((res) => {
          console.log(res.data, "res.datares.datares.data");
          onRecallOulet(true)
        })
        .catch((e) => {
          console.log(e);
        });
  }

  // Remove handleSave - verification now creates outlet and runs in background

  const resetForm = () => {
    setOutletName("");
    setBranch("");
    setLocationCode(2702);
    setLanguageName("English");
    setFetchMode("place_id");
    setInput("");
    setReviewLimit(10);
    setTripAdvisorUrl("");
    setTripAdvisorDepth(30);
    setFacebookUrl("");
    setEpicOutletId("");
    setWebhookSecret("");
    setVerifying(false);
    setTripAdvisorScraping(false);
    setFacebookScraping(false);
    setEpicScraping(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isEditMode ? 'Edit' : 'Add'} DataForSEO Outlet
            {/* <Badge variant="secondary">Beta</Badge> */}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Outlet Name */}
          <div>
            <Label htmlFor="selectOutlet">Outlet</Label>
            <Select value={outletName} onValueChange={(val) => setOutletName(val)}>
              <SelectTrigger id="selectOutlet" >
                <SelectValue placeholder="Select an outlet" />
              </SelectTrigger>
              <SelectContent>
                {outletList.map((outlet_: any) =>
                  outlet ?( outlet_.supabaseOutletID === outlet.id ? (
                    <SelectItem key={outlet_._id} value={outlet_._id}>
                      {outlet_.label}
                    </SelectItem>
                  ) : null // return null if not needed
                ):(
                  outlet_.supabaseOutletID === '' ? (
                    <SelectItem key={outlet_._id} value={outlet_._id}>
                      {outlet_.label}
                    </SelectItem>
                  ) : null
                ))}
                {/* outletList.map((outlet_:any) => (
                  <SelectItem key={outlet_._id} value={outlet_._id}>
                    {outlet_.label}
                  </SelectItem>
                )) */}
              </SelectContent>
            </Select>
          </div>
          {/* <div>
            <Label htmlFor="outlet-name">Outlet Name *</Label>
            <Input
              id="outlet-name"
              placeholder="e.g., Sakunthala's Restaurant - Race Course Outlet"
              value={outletName}
              onChange={(e) => setOutletName(e.target.value)}
            />
          </div> */}

          {/* Branch */}
          <div>
            <Label htmlFor="branch">Location / Branch</Label>
            <Input
              id="branch"
              placeholder="e.g., Race Course"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
            />
          </div>

          {/* Location & Language */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="location">Country</Label>
              <Select value={locationCode.toString()} onValueChange={(val) => {  setLocationCode(parseInt(val))}}>
                <SelectTrigger id="location">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {locationOptions.map((loc) => (
                    <SelectItem key={loc.code} value={loc.code.toString()}>
                      {loc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="language">Language</Label>
              <Select value={languageName} onValueChange={setLanguageName}>
                <SelectTrigger id="language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Chinese">Chinese</SelectItem>
                  <SelectItem value="Malay">Malay</SelectItem>
                  <SelectItem value="Tamil">Tamil</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Fetch Mode */}
          <div>
            <Label>Fetch Mode</Label>
            <RadioGroup value={fetchMode} onValueChange={(value) => setFetchMode(value as "keyword" | "place_id")}>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="keyword" id="keyword" />
                <Label htmlFor="keyword" className="font-normal cursor-pointer">Use Keyword</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="place_id" id="place_id" />
                <Label htmlFor="place_id" className="font-normal cursor-pointer">Use Place ID (Recommended)</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Input Field */}
          <div>
            <Label htmlFor="input">
              {fetchMode === "place_id" ? "Place ID or Google Maps URL *" : "Search Keyword *"}
            </Label>
            <Input
              id="input"
              placeholder={
                fetchMode === "place_id"
                  ? "ChIJ07EYSccZ2jERD86YcpLhjC8 or paste Google Maps URL"
                  : "e.g., Sakunthala's Restaurant Race Course"
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </div>

          {/* Review Limit */}
          <div>
            <Label htmlFor="review-limit">Review Limit</Label>
            <Input
              id="review-limit"
              type="number"
              min={1}
              max={100}
              value={reviewLimit}
              onChange={(e) => setReviewLimit(parseInt(e.target.value) || 10)}
            />
          </div>

          {/* TripAdvisor URL */}
          <div className="border-t pt-4 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="tripadvisor-url" className="flex items-center gap-2">
                ✈️ TripAdvisor URL (Optional)
              </Label>
              <Input
                id="tripadvisor-url"
                placeholder="https://www.tripadvisor.com/Restaurant_Review-..."
                value={tripAdvisorUrl}
                onChange={(e) => setTripAdvisorUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                💡 Add TripAdvisor URL to scrape reviews from both platforms
              </p>
            </div>

            {tripAdvisorUrl && (
              <div className="space-y-2">
                <Label htmlFor="tripadvisor-depth">TripAdvisor Review Limit</Label>
                <Input
                  id="tripadvisor-depth"
                  type="number"
                  min={10}
                  max={100}
                  step={5}
                  value={tripAdvisorDepth}
                  onChange={(e) => setTripAdvisorDepth(parseInt(e.target.value) || 30)}
                />
                <p className="text-xs text-muted-foreground">
                  Number of TripAdvisor reviews to fetch (10-100)
                </p>
              </div>
            )}
          </div>

          {/* Facebook URL */}
          <div className="border-t pt-4 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="facebook-url" className="flex items-center gap-2">
                📘 Facebook Page URL (Optional)
              </Label>
              <Input
                id="facebook-url"
                placeholder="https://www.facebook.com/YourRestaurant"
                value={facebookUrl}
                onChange={(e) => setFacebookUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                💡 Add Facebook Page URL to scrape reviews using Apify
              </p>
            </div>
          </div>

          {/* EpicReviews */}
          <div className="border-t pt-4 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="epic-outlet-id" className="flex items-center gap-2">
                🟥 EpicReviews (Low-Star Feedback)
              </Label>
              <Input
                id="epic-outlet-id"
                placeholder="Enter EpicPay Outlet ID"
                value={epicOutletId}
                onChange={(e) => setEpicOutletId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                💡 This is your outlet identifier in the EpicPay system
              </p>
            </div>
          </div>

          {/* Webhook Configuration (Edit mode only) */}
          {isEditMode && outlet?.id && (
            <div className="border-t border-dashed pt-4 space-y-3">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">🔐 Webhook Configuration</Label>
                <p className="text-xs text-muted-foreground">
                  Generate a secret and use it in EpicPay webhook signing.
                </p>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={webhookSecret || "Not generated yet"}
                    className="font-mono text-xs"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => {
                      if (!webhookSecret) return;
                      navigator.clipboard.writeText(webhookSecret);
                      toast({ title: "Copied", description: "Webhook secret copied" });
                    }}
                    disabled={!webhookSecret}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={async () => {
                    const secret = crypto.randomUUID().replace(/-/g, "") + crypto.randomUUID().replace(/-/g, "");
                    const { error } = await supabase
                      .from("outlets")
                      .update({ webhook_secret: secret })
                      .eq("id", outlet.id);

                    if (error) {
                      toast({
                        title: "Error",
                        description: error.message || "Failed to generate webhook secret",
                        variant: "destructive",
                      });
                      return;
                    }

                    setWebhookSecret(secret);
                    updateWebhooksupabase(secret)

                    toast({ title: "Success", description: "Webhook secret generated" });
                  }}
                >
                  {webhookSecret ? "🔄 Regenerate Secret" : "🔑 Generate Webhook Secret"}
                </Button>

                <div className="p-3 bg-muted rounded-md space-y-1">
                  <p className="text-xs font-medium">Webhook Endpoint:</p>
                  <code className="block text-xs break-all select-all p-2 bg-background rounded border">
                    {`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/epic-review-webhook`}
                  </code>
                </div>
              </div>
            </div>
          )}

          {/* Start Verification Button */}
          <Button onClick={handleVerify} disabled={verifying} className="w-full">
            {verifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              "🔍 Start Verification & Scraping"
            )}
          </Button>

          <p className="text-sm text-muted-foreground text-center">
            This will create the outlet and start scraping in the background. View progress in Sync Center.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
